import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Car, Zap, Battery, Leaf, Fuel, Loader2, TrendingUp, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useCarSuggestions, useDebounce, usePrefetchPopularBrands, type CarSuggestion, POPULAR_BRANDS } from '@/hooks/useCarSuggestions';
import { cn } from '@/lib/utils';
import { formatPrice } from '@/lib/formatters';
import { trackEvent } from '@/lib/analytics/ga';
import { Badge } from '@/components/ui/badge';

interface CarSearchAutocompleteProps {
  onSearch?: (query: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  defaultValue?: string;
  autoFocus?: boolean;
}

const powertrainIcons: Record<string, typeof Car> = {
  EV: Zap,
  PHEV: Battery,
  Hybrid: Leaf,
  ICE: Fuel,
};

interface ModelGroup {
  key: string; // brandSlug/modelSlug
  brandName: string;
  brandSlug: string;
  modelName: string;
  modelSlug: string;
  imageUrl: string | null;
  powertrainType: string;
  availabilityStatus: string;
  minPrice: number | null;
  maxPrice: number | null;
  priceIsExpected: boolean;
  minRange: number | null;
  maxRange: number | null;
  variants: CarSuggestion[];
}

// Flat list row for keyboard navigation
type FlatRow =
  | { type: 'model'; group: ModelGroup }
  | { type: 'variant'; suggestion: CarSuggestion; groupKey: string };

function buildModelGroups(suggestions: CarSuggestion[]): ModelGroup[] {
  const map = new Map<string, ModelGroup>();
  for (const s of suggestions) {
    const key = `${s.brandSlug}/${s.modelSlug}`;
    let g = map.get(key);
    if (!g) {
      g = {
        key,
        brandName: s.brandName,
        brandSlug: s.brandSlug,
        modelName: s.modelName,
        modelSlug: s.modelSlug,
        imageUrl: s.imageUrl,
        powertrainType: s.powertrainType,
        availabilityStatus: s.availabilityStatus,
        minPrice: null,
        maxPrice: null,
        priceIsExpected: false,
        minRange: null,
        maxRange: null,
        variants: [],
      };
      map.set(key, g);
    }
    g.variants.push(s);
    if (!g.imageUrl && s.imageUrl) g.imageUrl = s.imageUrl;

    const price = s.priceDkk ?? s.expectedPriceDkk ?? null;
    if (price !== null) {
      if (g.minPrice === null || price < g.minPrice) g.minPrice = price;
      if (g.maxPrice === null || price > g.maxPrice) g.maxPrice = price;
      if (s.priceDkk === null && s.expectedPriceDkk !== null) g.priceIsExpected = true;
    }
    if (s.rangeWltpKm !== null && s.rangeWltpKm !== undefined) {
      if (g.minRange === null || s.rangeWltpKm < g.minRange) g.minRange = s.rangeWltpKm;
      if (g.maxRange === null || s.rangeWltpKm > g.maxRange) g.maxRange = s.rangeWltpKm;
    }
  }
  // Derive group-level availability from variants: on_sale wins over upcoming.
  for (const g of map.values()) {
    const hasOnSale = g.variants.some(v => v.availabilityStatus === 'on_sale');
    const hasUpcoming = g.variants.some(v => v.availabilityStatus === 'upcoming');
    const hasDiscontinued = g.variants.some(v => v.availabilityStatus === 'discontinued');
    if (hasOnSale) g.availabilityStatus = 'on_sale';
    else if (hasUpcoming) g.availabilityStatus = 'upcoming';
    else if (hasDiscontinued) g.availabilityStatus = 'discontinued';
  }
  return Array.from(map.values());
}

function formatPriceRange(g: ModelGroup): string | null {
  if (g.minPrice === null) return null;
  const prefix = g.priceIsExpected ? 'Fra ca. ' : 'Fra ';
  if (g.maxPrice === null || g.minPrice === g.maxPrice) {
    return `${prefix}${formatPrice(g.minPrice)}`;
  }
  return `${prefix}${formatPrice(g.minPrice)} – ${formatPrice(g.maxPrice)}`;
}

function formatRangeRange(g: ModelGroup): string | null {
  if (g.minRange === null) return null;
  if (g.maxRange === null || g.minRange === g.maxRange) return `${g.minRange} km`;
  return `${g.minRange}–${g.maxRange} km`;
}

export function CarSearchAutocomplete({
  onSearch,
  placeholder = "Søg efter mærke, model eller variant...",
  className,
  inputClassName,
  defaultValue = '',
  autoFocus = false,
}: CarSearchAutocompleteProps) {
  const [inputValue, setInputValue] = useState(defaultValue);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [powertrainFilter, setPowertrainFilter] = useState<string | null>(null);
  const [expandedModels, setExpandedModels] = useState<Set<string>>(new Set());
  const debouncedQuery = useDebounce(inputValue, 300);
  const { data, isLoading } = useCarSuggestions({
    query: debouncedQuery,
    enabled: isOpen,
  });
  const suggestions: CarSuggestion[] = data?.suggestions ?? [];
  const didYouMean = data?.didYouMean ?? [];
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  usePrefetchPopularBrands();

  const filteredSuggestions = powertrainFilter
    ? suggestions.filter(s => s.powertrainType === powertrainFilter)
    : suggestions;

  // Group by brand → models
  const brandGroups = useMemo(() => {
    const byBrand = new Map<string, ModelGroup[]>();
    const models = buildModelGroups(filteredSuggestions);
    for (const m of models) {
      if (!byBrand.has(m.brandName)) byBrand.set(m.brandName, []);
      byBrand.get(m.brandName)!.push(m);
    }
    return byBrand;
  }, [filteredSuggestions]);

  // Build flat row list for keyboard navigation (respecting expansion)
  const flatRows = useMemo<FlatRow[]>(() => {
    const rows: FlatRow[] = [];
    for (const models of brandGroups.values()) {
      for (const g of models) {
        rows.push({ type: 'model', group: g });
        if (expandedModels.has(g.key) && g.variants.length > 1) {
          for (const v of g.variants) {
            rows.push({ type: 'variant', suggestion: v, groupKey: g.key });
          }
        }
      }
    }
    return rows;
  }, [brandGroups, expandedModels]);

  const availablePowertrains = [...new Set(suggestions.map(s => s.powertrainType))];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setSelectedIndex(-1);
  }, [flatRows.length]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsOpen(true);
  };

  const navigateToVariant = (s: CarSuggestion) => {
    trackEvent('search_used', { search_term: inputValue.slice(0, 100), result: s.variantSlug });
    navigate(`/biler/${s.brandSlug}/${s.modelSlug}/${s.variantSlug}`);
    setIsOpen(false);
    setInputValue('');
  };

  const handleModelClick = (g: ModelGroup) => {
    if (g.variants.length === 1) {
      navigateToVariant(g.variants[0]);
      return;
    }
    setExpandedModels(prev => {
      const next = new Set(prev);
      if (next.has(g.key)) next.delete(g.key);
      else next.add(g.key);
      return next;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' && inputValue.length >= 2) {
        setIsOpen(true);
        return;
      }
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < flatRows.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && flatRows[selectedIndex]) {
          const row = flatRows[selectedIndex];
          if (row.type === 'model') handleModelClick(row.group);
          else navigateToVariant(row.suggestion);
        } else if (onSearch) {
          onSearch(inputValue);
          setIsOpen(false);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const handleFocus = () => setIsOpen(true);

  const handleTrendingClick = (brand: string) => {
    setInputValue(brand);
    setIsOpen(true);
  };

  const showTrending = isOpen && inputValue.length < 2 && !isLoading;
  const showDropdown = isOpen && (filteredSuggestions.length > 0 || isLoading || inputValue.length >= 2 || showTrending);

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
        <Input
          ref={inputRef}
          type="text"
          aria-label="Søg efter mærke, model eller variant"
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          autoFocus={autoFocus}
          className={cn("pl-12", inputClassName)}
          autoComplete="off"
        />
        {isLoading && (
          <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground animate-spin" />
        )}
      </div>

      {showDropdown && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-popover border border-border rounded-lg shadow-lg z-[60] max-h-80 overflow-y-auto">
          {showTrending && (
            <div className="p-3">
              <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground mb-2">
                <TrendingUp className="w-3.5 h-3.5" />
                Populære søgninger
              </div>
              <div className="flex flex-wrap gap-2">
                {POPULAR_BRANDS.map((brand) => (
                  <button
                    key={brand}
                    onClick={() => handleTrendingClick(brand)}
                    className="px-3 py-1.5 text-sm bg-muted hover:bg-accent text-foreground rounded-full transition-colors"
                  >
                    {brand}
                  </button>
                ))}
              </div>
            </div>
          )}

          {isLoading && suggestions.length === 0 && inputValue.length >= 2 && (
            <div className="p-4 text-center text-sm text-muted-foreground">Søger...</div>
          )}

          {!isLoading && inputValue.length >= 2 && filteredSuggestions.length === 0 && (
            <div className="p-4 text-sm">
              <div className="text-center text-muted-foreground mb-3">
                {powertrainFilter
                  ? `Ingen ${powertrainFilter}-elbiler fundet for "${inputValue}"`
                  : `Ingen elbiler fundet for "${inputValue}"`}
              </div>
              {didYouMean.length > 0 && (
                <div>
                  <div className="text-xs font-medium text-muted-foreground mb-2 text-center">
                    Mente du:
                  </div>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {didYouMean.map((s) => (
                      <button
                        key={`${s.brandSlug}-${s.modelSlug}`}
                        onClick={() => {
                          setIsOpen(false);
                          navigate(
                            s.modelSlug
                              ? `/biler/${s.brandSlug}/${s.modelSlug}`
                              : `/biler/${s.brandSlug}`
                          );
                        }}

                        className="px-3 py-1.5 text-xs bg-muted hover:bg-accent text-foreground rounded-full transition-colors"
                      >
                        {s.brandName} {s.modelName}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!isLoading && inputValue.length >= 2 && filteredSuggestions.length > 0 && didYouMean.length > 0 && (
            <div className="px-3 py-2 border-b border-border">
              <div className="text-xs font-medium text-muted-foreground mb-2">Modeller uden varianter endnu</div>
              <div className="flex flex-wrap gap-2">
                {didYouMean.map((s) => (
                  <button
                    key={`model-${s.brandSlug}-${s.modelSlug}`}
                    onClick={() => {
                      setIsOpen(false);
                      navigate(`/biler/${s.brandSlug}/${s.modelSlug}`);
                    }}
                    className="px-3 py-1.5 text-xs bg-muted hover:bg-accent text-foreground rounded-full transition-colors"
                  >
                    {s.brandName} {s.modelName}
                  </button>
                ))}
              </div>
            </div>
          )}


          {suggestions.length > 0 && availablePowertrains.length > 1 && (
            <div className="px-3 py-2 border-b border-border flex flex-wrap gap-1.5">
              <button
                onClick={() => setPowertrainFilter(null)}
                className={cn(
                  "px-2.5 py-1 text-xs rounded-full transition-colors",
                  powertrainFilter === null
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground hover:bg-accent"
                )}
              >
                Alle
              </button>
              {availablePowertrains.map((pt) => {
                const Icon = powertrainIcons[pt] || Car;
                const count = suggestions.filter(s => s.powertrainType === pt).length;
                return (
                  <button
                    key={pt}
                    onClick={() => setPowertrainFilter(pt === powertrainFilter ? null : pt)}
                    className={cn(
                      "px-2.5 py-1 text-xs rounded-full transition-colors flex items-center gap-1",
                      pt === powertrainFilter
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-accent"
                    )}
                  >
                    <Icon className="w-3 h-3" />
                    {pt} ({count})
                  </button>
                );
              })}
            </div>
          )}

          {Array.from(brandGroups.entries()).map(([brandName, models]) => (
            <div key={brandName}>
              <div className="px-3 py-2 text-xs font-medium text-muted-foreground bg-muted/50 sticky top-0">
                {brandName}
              </div>
              {models.map((g) => {
                const modelRowIndex = flatRows.findIndex(
                  (r) => r.type === 'model' && r.group.key === g.key,
                );
                const Icon = powertrainIcons[g.powertrainType] || Car;
                const expanded = expandedModels.has(g.key) && g.variants.length > 1;
                const priceLabel = formatPriceRange(g);
                const rangeLabel = formatRangeRange(g);

                return (
                  <div key={g.key}>
                    <button
                      onClick={() => handleModelClick(g)}
                      className={cn(
                        "w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-accent transition-colors",
                        modelRowIndex === selectedIndex && "bg-accent"
                      )}
                    >
                      <div className="w-12 h-12 rounded-md bg-muted flex items-center justify-center flex-shrink-0 overflow-hidden">
                        {g.imageUrl ? (
                          <img
                            src={g.imageUrl}
                            alt={`${g.brandName} ${g.modelName}`}
                            loading="lazy"
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <Icon className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="text-sm font-medium truncate text-foreground">
                            {g.modelName}
                          </div>
                          {g.availabilityStatus === 'upcoming' && (
                            <Badge variant="upcoming" className="text-[10px] px-1.5 py-0 flex-shrink-0">
                              Kommende
                            </Badge>
                          )}
                          {g.availabilityStatus === 'on_sale' && g.variants.some(v => v.availabilityStatus === 'upcoming') && (
                            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 flex-shrink-0">
                              +{g.variants.filter(v => v.availabilityStatus === 'upcoming').length} kommer
                            </Badge>
                          )}
                          {g.availabilityStatus === 'discontinued' && (
                            <Badge variant="discontinued" className="text-[10px] px-1.5 py-0 flex-shrink-0">
                              Udgået
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                          <span>{g.brandName}</span>
                          {priceLabel && (
                            <>
                              <span className="text-border">•</span>
                              <span className="font-medium text-foreground">{priceLabel}</span>
                            </>
                          )}
                          {rangeLabel && (
                            <>
                              <span className="text-border">•</span>
                              <span>{rangeLabel}</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {g.variants.length > 1 && (
                          <>
                            <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                              {g.variants.length} varianter
                            </span>
                            <ChevronDown
                              className={cn(
                                "w-4 h-4 text-muted-foreground transition-transform",
                                expanded && "rotate-180"
                              )}
                            />
                          </>
                        )}
                      </div>
                    </button>

                    {expanded && g.variants.map((v) => {
                      const variantRowIndex = flatRows.findIndex(
                        (r) => r.type === 'variant' && r.suggestion.variantId === v.variantId,
                      );
                      return (
                        <button
                          key={v.variantId}
                          onClick={() => navigateToVariant(v)}
                          className={cn(
                            "w-full flex items-center gap-3 pl-12 pr-3 py-2 text-left hover:bg-accent transition-colors border-l-2 border-border ml-3",
                            variantRowIndex === selectedIndex && "bg-accent"
                          )}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm truncate text-foreground">
                              {v.variantName || g.modelName}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              {v.priceDkk ? (
                                <span className="font-medium text-foreground">{formatPrice(v.priceDkk)}</span>
                              ) : v.expectedPriceDkk ? (
                                <span className="font-medium text-foreground">Fra ca. {formatPrice(v.expectedPriceDkk)}</span>
                              ) : null}
                              {v.rangeWltpKm && (
                                <>
                                  <span className="text-border">•</span>
                                  <span>{v.rangeWltpKm} km</span>
                                </>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}

          {inputValue.length >= 2 && filteredSuggestions.length > 0 && onSearch && (
            <button
              onClick={() => {
                onSearch(inputValue);
                setIsOpen(false);
              }}
              className={cn(
                "w-full px-3 py-3 text-sm text-center text-primary hover:bg-accent border-t border-border",
                selectedIndex === -1 && "font-medium"
              )}
            >
              Se alle resultater for "{inputValue}"
            </button>
          )}
        </div>
      )}
    </div>
  );
}
